import clsx from "clsx";
import { useState } from "react";
import { FieldArrayWithId } from "react-hook-form";

import { UpdateHookValues } from "@smart/itops-hooks-dom";
import {
  Button,
  DragHandleProps,
  EditableText,
  Icon,
} from "@smart/itops-sb-design-system-dom";

import {
  optionContainerClasses,
  optionItemEditClasses,
  optionItemIndicatorClasses,
} from "./class-names";

export type OptionsProps = {
  allowCustomResponse?: boolean | null | undefined;
  options: { label: string; value: string }[];
};

export type OptionsSharedProps = {
  type: "choice" | "checkbox";
  optionsFields: FieldArrayWithId<
    UpdateHookValues<OptionsProps>,
    "options",
    "id"
  >[];
  removeOption: (index?: number) => Promise<void>;
  updateOption: (index: number, label: string | undefined) => Promise<void>;
};

export type DraggableOptionItemProps = OptionsSharedProps & {
  index: number;
  dragHandleProps: DragHandleProps;
  newOptionIndex: number | undefined;
  isDragging: boolean;
  disabled?: boolean;
};

export const DraggableOptionItem = ({
  index,
  dragHandleProps,
  type,
  removeOption,
  updateOption,
  optionsFields,
  newOptionIndex,
  isDragging,
  disabled,
}: DraggableOptionItemProps) => {
  const hasMultipleOptions = optionsFields.length > 1;

  const [editingTitle, setEditingTitle] = useState(
    optionsFields[index]?.label || undefined,
  );

  return (
    <div
      className={clsx(optionContainerClasses, isDragging && "opacity-50")}
      data-testid={`option-item-${index}`}
    >
      <div className="flex">
        <div
          {...(disabled
            ? {}
            : {
                ...dragHandleProps.attributes,
                ...dragHandleProps.listeners,
              })}
          className={clsx(
            "flex justify-center items-center opacity-0 transition duration-200 group-hover:opacity-100",
            !hasMultipleOptions && "invisible pointer-events-none",
            disabled && "cursor-not-allowed",
          )}
          data-testid="option-drag-handle"
        >
          <Icon name="solidGripDotsVertical" className="w-[0.6rem] px-2" />
        </div>
        <div
          className={optionItemIndicatorClasses(type === "choice")}
          data-testid={`select-icon-${index}`}
        />
      </div>
      <div className={optionItemEditClasses}>
        <EditableText
          text={editingTitle || ""}
          onChange={setEditingTitle}
          placeholder="Option"
          onComplete={async () => {
            // Prevent updating when the empty option is re-rendered
            if (editingTitle === "" && optionsFields[index]?.label === "")
              return;
            await updateOption(index, editingTitle);
          }}
          editing={newOptionIndex === index}
          fontWeight="semibold"
        />
      </div>
      {hasMultipleOptions && (
        <Button
          leftIcon="regularXmark"
          variant="secondarySubtle"
          dataTestId={`delete-icon-option-${index}`}
          size="small"
          onClick={async () => {
            await removeOption(index);
          }}
          disabled={disabled}
        />
      )}
    </div>
  );
};
